import { apiClient } from '@/lib/api-client';
import type { Outcome } from './api';
import type { Report } from './types';

type Answer = Promise<{ data?: unknown; error?: unknown; response: Response }>;

async function call<T>(run: () => Answer): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok) return { ok: true, value: data as T };
    const code =
      typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
    return { ok: false, status: response.status, code };
  } catch {
    return { ok: false, status: 0, code: '' };
  }
}

/** One report waiting for the caller's decision (the queue's row, B2). */
export type QueueItem = {
  id: string;
  number: string;
  title: string;
  periodMonth: string;
  owner: { userId: string; displayName: string };
  stage: 1 | 2;
  version: string;
  expenseCount: number;
  total: string;
  reimbursable: string;
  submittedAt?: string | null;
  waitingSince: string;
  waitingDays: number;
};
export type QueueLevel = { count: number; items: QueueItem[] };
export type ApprovalQueue = { firstLevel: QueueLevel; secondLevel: QueueLevel };

export function getApprovalQueue(): Promise<Outcome<ApprovalQueue>> {
  return call<ApprovalQueue>(() => apiClient.GET('/v1/expenses/reports/approvals'));
}

export type ReportDecision = 'approve' | 'return' | 'refuse';

/** The caller's decision on the whole report; a note is optional only for «approve». */
export function decideReport(
  kind: ReportDecision,
  reportId: string,
  version: string,
  note: string,
): Promise<Outcome<Report>> {
  const params = { path: { reportId }, header: { 'If-Match': version } };
  const text = note.trim();
  if (kind === 'approve') {
    const body = text === '' ? {} : { note: text };
    return call<Report>(() =>
      apiClient.POST('/v1/expenses/reports/{reportId}/approve', { params, body }),
    );
  }
  const path =
    kind === 'return'
      ? '/v1/expenses/reports/{reportId}/return'
      : '/v1/expenses/reports/{reportId}/refuse';
  return call<Report>(() => apiClient.POST(path, { params, body: { note: text } }));
}

export type RowDecision = 'reject' | 'make-reimbursable';

/** The caller's decision on one expense of the report; the note is mandatory. */
export function decideExpense(
  kind: RowDecision,
  reportId: string,
  expenseId: string,
  version: string,
  note: string,
): Promise<Outcome<Report>> {
  const params = { path: { reportId, expenseId }, header: { 'If-Match': version } };
  const body = { note: note.trim() };
  const path =
    kind === 'reject'
      ? '/v1/expenses/reports/{reportId}/expenses/{expenseId}/reject'
      : '/v1/expenses/reports/{reportId}/expenses/{expenseId}/make-reimbursable';
  return call<Report>(() => apiClient.POST(path, { params, body }));
}
